'use client';

import type {
  MysteryRoomIncidentKind,
  MysteryRoomIncidentState,
  MysteryRoomLocalRuntime,
  MysteryRoomNpcState,
  MysteryRoomSecurityState,
} from '../core/localRuntime';
import type { MysteryRoomVec2 } from '../world/mapTypes';
import { buildSightBlockingSegments, hasUnobstructedSightLine } from './visibilitySystem';
import { normalizeInputVector } from './inputSystem';

const TRESPASS_WARNING_MS = 3_500;
const BODY_REPORT_MIN_RANGE = 360;

type SecurityIncidentInput = {
  id: string;
  kind: MysteryRoomIncidentKind;
  zoneId: string;
  position: MysteryRoomVec2;
  sourceActorId: string;
  severity: number;
};

export function updateSecuritySystems(runtime: MysteryRoomLocalRuntime): MysteryRoomLocalRuntime {
  return updateBodyReports(updateTrespassResponse(runtime));
}

function updateTrespassResponse(runtime: MysteryRoomLocalRuntime): MysteryRoomLocalRuntime {
  const unauthorizedRoom = runtime.map.rooms.find(
    (room) => room.id === runtime.hero.zoneId && !runtime.hero.accessLevels.includes(room.accessLevel),
  );
  const activeTrespass = runtime.security.trespass;

  if (!unauthorizedRoom) {
    if (!activeTrespass) {
      return runtime;
    }

    return {
      ...runtime,
      security: {
        ...runtime.security,
        trespass: null,
      },
      events: [`Предупреждение снято: ${activeTrespass.zoneTitle}`, ...runtime.events].slice(0, 10),
    };
  }

  if (activeTrespass?.zoneId === unauthorizedRoom.id) {
    if (runtime.elapsedMs < activeTrespass.warningUntilMs || activeTrespass.escalatedAtMs !== null) {
      return runtime;
    }

    const guard = findAliveGuard(runtime, activeTrespass.detectedByNpcId) ?? findAnyAliveGuard(runtime);
    if (!guard) {
      return runtime;
    }

    return {
      ...runtime,
      incidents: upsertAlarmCallIncident(runtime, runtime.incidents, {
        id: `alarm_call_trespass_${unauthorizedRoom.id}`,
        sourceActorId: guard.id,
        severity: 0.96,
      }),
      security: {
        ...runtime.security,
        trespass: {
          ...activeTrespass,
          escalatedAtMs: runtime.elapsedMs,
        },
      },
      events: [`Охрана идёт к кнопке тревоги: ${unauthorizedRoom.title}`, ...runtime.events].slice(0, 10),
    };
  }

  const guard = findGuardSeeingHero(runtime);
  if (!guard) {
    return activeTrespass
      ? {
          ...runtime,
          security: {
            ...runtime.security,
            trespass: null,
          },
        }
      : runtime;
  }

  return {
    ...runtime,
    security: {
      ...runtime.security,
      trespass: {
        zoneId: unauthorizedRoom.id,
        zoneTitle: unauthorizedRoom.title,
        requiredAccess: unauthorizedRoom.accessLevel,
        detectedByNpcId: guard.id,
        warningStartedAtMs: runtime.elapsedMs,
        warningUntilMs: runtime.elapsedMs + TRESPASS_WARNING_MS,
        escalatedAtMs: null,
      },
    },
    events: [`Предупреждение охраны: покиньте ${unauthorizedRoom.title}`, ...runtime.events].slice(0, 10),
  };
}

function updateBodyReports(runtime: MysteryRoomLocalRuntime): MysteryRoomLocalRuntime {
  const reportedNpcIds = new Set(runtime.security.bodyReports.map((report) => report.npcId));
  const blockingSegments = buildSightBlockingSegments(runtime.map, runtime.doors);
  let incidents = runtime.incidents;
  let security: MysteryRoomSecurityState = runtime.security;
  const nextEvents: string[] = [];

  for (const victim of runtime.npcs) {
    if (victim.alive || victim.killedAtMs === null || reportedNpcIds.has(victim.id)) {
      continue;
    }

    const reporter = runtime.npcs.find(
      (npc) => npc.alive && npc.id !== victim.id && npcCanSeePoint(npc, victim.position, blockingSegments),
    );
    if (!reporter) {
      continue;
    }

    const guard = findAnyAliveGuard(runtime);
    const medic = runtime.npcs.find((npc) => npc.alive && npc.role === 'medic') ?? null;
    const guardCalled = guard !== null;
    const medicCalled = medic !== null;

    security = {
      ...security,
      bodyReports: [
        ...security.bodyReports,
        {
          npcId: victim.id,
          discoveredByNpcId: reporter.id,
          discoveredAtMs: runtime.elapsedMs,
          guardCalled,
          medicCalled,
        },
      ],
    };
    reportedNpcIds.add(victim.id);

    if (guardCalled) {
      incidents = upsertAlarmCallIncident(runtime, incidents, {
        id: `alarm_call_body_${victim.id}`,
        sourceActorId: reporter.id,
        severity: 1,
      });
    }

    if (medicCalled) {
      incidents = upsertSecurityIncident(incidents, {
        id: `body_medical_${victim.id}`,
        kind: 'injured_person',
        zoneId: victim.zoneId,
        position: { ...victim.position },
        sourceActorId: reporter.id,
        severity: 0.92,
      }, runtime.elapsedMs);
    }

    const responseLabel = guardCalled && medicCalled
      ? 'вызваны охрана и медик'
      : guardCalled
        ? 'вызвана охрана'
        : medicCalled
          ? 'вызван медик'
          : 'помощь недоступна';

    nextEvents.push(`Обнаружен нейтрализованный сотрудник: ${victim.name}; ${responseLabel}`);
  }

  if (security === runtime.security && incidents === runtime.incidents) {
    return runtime;
  }

  return {
    ...runtime,
    incidents,
    security,
    events: [...nextEvents.reverse(), ...runtime.events].slice(0, 10),
  };
}

function findGuardSeeingHero(runtime: MysteryRoomLocalRuntime) {
  if (runtime.hero.hidden) {
    return null;
  }

  const blockingSegments = buildSightBlockingSegments(runtime.map, runtime.doors);
  return runtime.npcs.find(
    (npc) => npc.alive && npc.role === 'guard' && npcCanSeePoint(npc, runtime.hero.position, blockingSegments),
  ) ?? null;
}

function findAliveGuard(runtime: MysteryRoomLocalRuntime, npcId: string) {
  return runtime.npcs.find((npc) => npc.id === npcId && npc.alive && npc.role === 'guard') ?? null;
}

function findAnyAliveGuard(runtime: MysteryRoomLocalRuntime) {
  return runtime.npcs.find((npc) => npc.alive && npc.role === 'guard') ?? null;
}

function upsertAlarmCallIncident(
  runtime: MysteryRoomLocalRuntime,
  incidents: MysteryRoomIncidentState[],
  input: {
    id: string;
    sourceActorId: string;
    severity: number;
  },
) {
  const alarmAnchor = runtime.map.incidentAnchors.find((anchor) => anchor.id === 'alarm_cross');

  return upsertSecurityIncident(incidents, {
    id: input.id,
    kind: 'alarm_call',
    zoneId: alarmAnchor?.zoneId ?? 'central_cross',
    position: alarmAnchor ? { ...alarmAnchor.position } : { x: 2048, y: 1080 },
    sourceActorId: input.sourceActorId,
    severity: input.severity,
  }, runtime.elapsedMs);
}

function upsertSecurityIncident(
  incidents: MysteryRoomIncidentState[],
  input: SecurityIncidentInput,
  nowMs: number,
) {
  const existingIncident = incidents.find((incident) => incident.id === input.id);
  if (!existingIncident) {
    return [
      ...incidents,
      {
        id: input.id,
        kind: input.kind,
        zoneId: input.zoneId,
        position: { ...input.position },
        sourceType: 'system' as const,
        sourceActorId: input.sourceActorId,
        severity: input.severity,
        status: 'active' as const,
        assignedNpcId: null,
        leaseUntilMs: 0,
        createdAtMs: nowMs,
        resolvedAtMs: null,
        resolutionAction: null,
        resolutionRemainingMs: 0,
      },
    ];
  }

  if (existingIncident.status === 'resolved' || existingIncident.kind !== input.kind) {
    return incidents;
  }

  return incidents.map((incident) =>
    incident.id === input.id
      ? {
          ...incident,
          zoneId: input.zoneId,
          position: { ...input.position },
          sourceActorId: input.sourceActorId,
          severity: Math.max(incident.severity, input.severity),
        }
      : incident,
  );
}

function npcCanSeePoint(
  npc: MysteryRoomNpcState,
  point: MysteryRoomVec2,
  blockingSegments: ReturnType<typeof buildSightBlockingSegments>,
) {
  const delta = {
    x: point.x - npc.position.x,
    y: point.y - npc.position.y,
  };
  const distance = Math.hypot(delta.x, delta.y);
  const visionRange = Math.max(BODY_REPORT_MIN_RANGE, npc.visionRange);

  if (distance > visionRange) {
    return false;
  }

  if (!pointInsideViewCone(npc.position, npc.facing, npc.visionHalfAngleDeg, point)) {
    return false;
  }

  return hasUnobstructedSightLine(npc.position, point, blockingSegments);
}

function pointInsideViewCone(
  origin: MysteryRoomVec2,
  facing: MysteryRoomNpcState['facing'],
  halfAngleDeg: number,
  target: MysteryRoomVec2,
) {
  const facingVector = normalizeInputVector(facingToVector(facing));
  const toTarget = normalizeInputVector({
    x: target.x - origin.x,
    y: target.y - origin.y,
  });
  const dot = facingVector.x * toTarget.x + facingVector.y * toTarget.y;

  return dot >= Math.cos((halfAngleDeg * Math.PI) / 180);
}

function facingToVector(facing: MysteryRoomNpcState['facing']) {
  switch (facing) {
    case 'up':
      return { x: 0, y: -1 };
    case 'down':
      return { x: 0, y: 1 };
    case 'left':
      return { x: -1, y: 0 };
    case 'right':
      return { x: 1, y: 0 };
  }
}
